/**
 * Global SEO Head Component
 * 
 * Handles canonical URLs and robots meta tags for ALL pages automatically.
 * Include ONCE in App.js - applies to every page.
 * 
 * CANONICAL RULES:
 * - Homepage (/): https://elido.nu/
 * - Public pages (/hemsida-malare, /blog, etc): https://elido.nu/{path}
 * - Query params stripped: /gratis-demo?x=y -> https://elido.nu/gratis-demo
 * - Demo pages (/demo, /d/*): https://elido.nu/ (point to homepage)
 * - Admin/auth pages: NO canonical tag
 * 
 * ROBOTS RULES:
 * - Public SEO pages: index,follow
 * - /demo, /d/*: noindex,nofollow
 * - /admin/*, /login, /auth/*: noindex,nofollow
 * - /start (configurator): noindex,nofollow
 * 
 * IMPORTANT: Only ONE canonical tag per page. Non-www only.
 */

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

const PRIMARY_DOMAIN = 'https://elido.nu';

/**
 * Check if page is a demo page (gets homepage canonical)
 */
function isDemoPage(pathname) {
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  return cleanPath === '/demo' || cleanPath.startsWith('/d/');
}

/**
 * Check if page should have NO canonical tag at all
 */
function shouldExcludeCanonical(pathname) {
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  
  // Admin pages - no canonical
  if (cleanPath === '/admin' || cleanPath.startsWith('/admin/')) return true;
  
  // Auth pages - no canonical
  if (cleanPath === '/login' || cleanPath.startsWith('/auth')) return true;
  
  // Configurator - no canonical
  if (cleanPath === '/start') return true;

  // /ny-hemsida — Google Ads-landningssidan äger sin egen canonical +
  // robots via <Helmet> i NyHemsidaPage.jsx. Låt inte GlobalSEOHead
  // skriva över det med index,follow.
  if (cleanPath === '/ny-hemsida') return true;

  // /gratis-demo — dedikerad lead-gen-sida äger sin egen canonical
  // via <Helmet> i GratisDemoPage.jsx.
  if (cleanPath === '/gratis-demo') return true;

  return false;
}

/**
 * Generate canonical URL based on pathname
 * - Public pages: self-referencing canonical
 * - Demo pages: homepage canonical
 * - Admin/auth: no canonical
 */
export function getCanonicalUrl(pathname) {
  // Clean pathname - remove trailing slash except for root
  let cleanPath = pathname.replace(/\/+$/, '') || '/';
  
  // Remove hash fragments
  if (cleanPath.includes('#')) {
    cleanPath = cleanPath.split('#')[0];
  }
  
  // Admin/auth pages - no canonical
  if (shouldExcludeCanonical(cleanPath)) {
    return null;
  }
  
  // Demo pages - point to homepage
  if (isDemoPage(cleanPath)) {
    return PRIMARY_DOMAIN + '/';
  }
  
  // Homepage
  if (cleanPath === '/') {
    return PRIMARY_DOMAIN + '/';
  }
  
  // All other public pages: self-referencing canonical
  return PRIMARY_DOMAIN + cleanPath;
}

/**
 * Determine robots meta content based on pathname
 */
export function getRobotsContent(pathname) {
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  
  // Demo pages - noindex
  if (isDemoPage(cleanPath)) {
    return 'noindex,nofollow';
  }
  
  // Admin pages - noindex
  if (cleanPath === '/admin' || cleanPath.startsWith('/admin/')) {
    return 'noindex,nofollow';
  }
  
  // Auth pages - noindex
  if (cleanPath === '/login' || cleanPath.startsWith('/auth')) {
    return 'noindex,nofollow';
  }
  
  // Configurator - noindex
  if (cleanPath === '/start') {
    return 'noindex,nofollow';
  }

  // /ny-hemsida — Google Ads landing. GlobalSEOHead skulle annars sätta
  // index,follow. Sidan sätter sin egen `noindex,follow` via <Helmet>.
  if (cleanPath === '/ny-hemsida') {
    return 'noindex,follow';
  }

  // All other pages - index
  return 'index,follow';
}

/**
 * Global SEO Head Component
 * Automatically handles canonical and robots for all pages
 */
export function GlobalSEOHead() {
  const location = useLocation();
  const pathname = location.pathname;
  const [cmsRobots, setCmsRobots] = useState(null);

  // Stored CMS pages may be publicly readable but intentionally noindex.
  // The URL alone cannot reveal that state, so ask the existing SEO resolver.
  // Only check single-slug routes; admin/auth and other known routes keep
  // their route-level robots rules without extra network requests.
  useEffect(() => {
    const clean = pathname.replace(/\/+$/, '') || '/';
    if (!/^\/[a-z0-9][a-z0-9-]*$/i.test(clean) ||
        clean === '/ny-hemsida' || clean === '/gratis-demo') {
      setCmsRobots(null);
      return undefined;
    }
    const controller = new AbortController();
    fetch('/api/seo/resolve?path=' + encodeURIComponent(clean), { signal: controller.signal })
      .then(response => response.ok ? response.json() : null)
      .then(data => {
        if (!controller.signal.aborted) {
          setCmsRobots({ pathname: clean, noindex: data?.action === 'noindex' });
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [pathname]);

  const canonicalUrl = getCanonicalUrl(pathname);
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  const robotsContent = cmsRobots?.pathname === cleanPath && cmsRobots.noindex
    ? 'noindex,nofollow'
    : getRobotsContent(pathname);

  // /ny-hemsida äger sin egen head via <Helmet> i NyHemsidaPage.jsx.
  // Vi returnerar null här så att robots+canonical inte injiceras en
  // andra gång från GlobalSEOHead → förhindrar dubbletter efter hydrering.
  if (pathname.replace(/\/+$/, '') === '/ny-hemsida') {
    return null;
  }

  // /gratis-demo äger också sin egen head via <Helmet> i GratisDemoPage.jsx.
  if (pathname.replace(/\/+$/, '') === '/gratis-demo') {
    return null;
  }

  return (
    <Helmet>
      {/* Only render canonical if not excluded */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content={robotsContent} />
    </Helmet>
  );
}

export default GlobalSEOHead;
